import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, ArrowRight, Mail, Lock, User, Loader2 } from 'lucide-react';
import NoDoublesLogo from '@/components/marketing/NoDoublesLogo';
import { PasswordStrengthMeter } from '@/components/auth/PasswordStrengthMeter';
import { SocialAuthButtons } from '@/components/auth/SocialAuthButtons';
import { calculatePasswordStrength } from '@/lib/auth/passwordStrength';

const SOCIAL_AUTH_ENABLED =
  import.meta.env.VITE_ENABLE_GOOGLE_AUTH === 'true' ||
  import.meta.env.VITE_ENABLE_APPLE_AUTH === 'true';

const signInSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

const signUpSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(50),
  lastName: z.string().min(1, 'Last name is required').max(50),
  nickname: z.string().min(1, 'Display name is required').max(50),
  email: z.string().email('Please enter a valid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .refine((v) => /[A-Z]/.test(v), 'Must contain an uppercase letter')
    .refine((v) => /[a-z]/.test(v), 'Must contain a lowercase letter')
    .refine((v) => /\d/.test(v), 'Must contain a number'),
  handicap: z.string().optional(),
  ageBracket: z.enum(['under13', '13-17', '18+'], {
    errorMap: () => ({ message: 'Please select your age range' }),
  }),
  agreedToTerms: z.literal(true, {
    errorMap: () => ({ message: 'You must agree to the Terms and Privacy Policy' }),
  }),
});

type SignInValues = z.infer<typeof signInSchema>;
type SignUpValues = z.infer<typeof signUpSchema>;

const sanitizeInput = (value: string) => value.trim().replace(/[<>]/g, '');

export default function Auth() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'signin' | 'signup'>('signin');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordBreached, setPasswordBreached] = useState(false);
  const { toast } = useToast();

  const signInForm = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: '', password: '' },
  });

  const signUpForm = useForm<SignUpValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      nickname: '',
      email: '',
      password: '',
      handicap: '',
      ageBracket: undefined as unknown as '18+',
      agreedToTerms: undefined as unknown as true,
    },
  });

  const handleSignIn = async (values: SignInValues) => {
    setIsLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: values.email,
      password: values.password,
    });

    if (error) {
      let message = 'Sign in failed. Please check your credentials.';
      if (error.message.includes('Invalid login credentials')) {
        message = 'Invalid email or password. Please check your credentials and try again.';
      } else if (error.message.includes('Email not confirmed')) {
        message = 'Please check your email and click the verification link before signing in.';
      }
      toast({ title: 'Sign In Failed', description: message, variant: 'destructive' });
    } else {
      toast({ title: 'Welcome back!', description: 'You have successfully signed in.' });
    }
    setIsLoading(false);
  };

  const handleSignUp = async (values: SignUpValues) => {
    setIsLoading(true);

    if (values.ageBracket === 'under13') {
      toast({
        title: 'Age Requirement',
        description: 'You must be at least 13 years old to use No Doubles.',
        variant: 'destructive',
      });
      setIsLoading(false);
      return;
    }

    const strength = calculatePasswordStrength(values.password);
    if (!strength.allRequirementsMet) {
      toast({
        title: 'Password Requirements',
        description: 'Please meet all password requirements.',
        variant: 'destructive',
      });
      setIsLoading(false);
      return;
    }

    const lastAttempt = localStorage.getItem('lastSignupAttempt');
    const now = Date.now();
    if (lastAttempt && now - parseInt(lastAttempt) < 5000) {
      toast({
        title: 'Please Wait',
        description: 'Please wait a moment before trying again.',
        variant: 'destructive',
      });
      setIsLoading(false);
      return;
    }
    localStorage.setItem('lastSignupAttempt', now.toString());

    const isMinor = values.ageBracket === '13-17';

    try {
      const first = sanitizeInput(values.firstName);
      const last = sanitizeInput(values.lastName);
      const userMetadata: Record<string, string | boolean> = {
        first_name: first,
        last_name: last,
        full_name: `${first} ${last}`,
        is_minor: isMinor,
      };
      if (values.nickname.trim()) {
        userMetadata.nickname = values.nickname.trim();
      }
      if (values.handicap) {
        userMetadata.handicap = sanitizeInput(values.handicap);
      }

      const { data, error } = await supabase.auth.signUp({
        email: values.email.toLowerCase().trim(),
        password: values.password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: userMetadata,
        },
      });

      if (data?.user) {
        const profileUpdate: Record<string, any> = {
          first_name: first,
          last_name: last,
          terms_accepted_at: new Date().toISOString(),
          terms_version: '2026-03-01',
          is_minor: isMinor,
        };
        if (values.nickname.trim()) {
          profileUpdate.nickname = values.nickname.trim();
        }
        await supabase
          .from('profiles')
          .update(profileUpdate)
          .eq('user_id', data.user.id);
      }

      if (error) {
        let message = 'Unable to create account. Please try again.';
        if (error.message.includes('already registered') || error.message.includes('already exists')) {
          message = 'An account with this email already exists. Please sign in instead.';
        } else if (error.message.includes('password')) {
          message = 'Password requirements not met.';
        } else if (error.message.includes('rate limit')) {
          message = 'Too many signup attempts. Please wait before trying again.';
        }
        toast({ title: 'Sign Up Failed', description: message, variant: 'destructive' });
      } else if (data?.user && !data.user.email_confirmed_at) {
        toast({
          title: 'Account Created!',
          description: 'Please check your email and click the verification link to complete your registration.',
        });
        signUpForm.reset();
      } else if (data?.user) {
        toast({
          title: 'Account Created!',
          description: "Welcome to No Doubles Golf Co! You're now signed in.",
        });
        signUpForm.reset();
      }
    } catch {
      toast({
        title: 'Unexpected Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    }
    setIsLoading(false);
  };

  const watchedPassword = signUpForm.watch('password');

  return (
    <div className="min-h-screen bg-gradient-to-b from-surface to-muted flex flex-col justify-start md:justify-center px-4 py-6 md:py-12 overflow-y-auto">
      <div className="w-full max-w-md mx-auto flex-shrink-0">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-8 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back to home</span>
        </button>

        {/* Logo Section */}
        <div className="text-center mb-8">
          <NoDoublesLogo size={100} className="mx-auto mb-4" />
          <p className="text-muted-foreground text-lg">The Smarter Way to Lower Your Scores</p>
        </div>

        {/* Auth Card */}
        <div className="bg-card rounded-2xl shadow-lg overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-sun-gold to-fairway-light" />

          <div className="p-8 md:p-10">
            <Tabs
              value={activeTab}
              onValueChange={(v) => setActiveTab(v as 'signin' | 'signup')}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2 gap-0 mb-8 bg-muted rounded-xl p-1">
                <TabsTrigger
                  value="signin"
                  className="rounded-lg data-[state=active]:bg-card data-[state=active]:shadow-sm transition-all duration-200 py-3 font-semibold data-[state=active]:text-foreground data-[state=inactive]:text-muted-foreground"
                >
                  Sign In
                </TabsTrigger>
                <TabsTrigger
                  value="signup"
                  className="rounded-lg data-[state=active]:bg-card data-[state=active]:shadow-sm transition-all duration-200 py-3 font-semibold data-[state=active]:text-foreground data-[state=inactive]:text-muted-foreground"
                >
                  Sign Up
                </TabsTrigger>
              </TabsList>

              {/* ── Sign In Tab ── */}
              <TabsContent value="signin" className="space-y-6 mt-0">
                <div>
                  <h2 className="text-2xl font-bold text-foreground mb-2">Welcome back</h2>
                  <p className="text-muted-foreground text-sm mb-6">
                    Sign in to continue your golf improvement journey
                  </p>
                </div>

                <SocialAuthButtons disabled={isLoading} />

                {SOCIAL_AUTH_ENABLED && (
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
                    </div>
                  </div>
                )}

                <Form {...signInForm}>
                  <form onSubmit={signInForm.handleSubmit(handleSignIn)} className="space-y-5">
                    <FormField
                      control={signInForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-foreground">Email</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none z-10" />
                              <Input
                                {...field}
                                type="email"
                                placeholder="you@example.com"
                                className="pl-10 h-12 border-border focus:border-primary focus:ring-primary"
                                disabled={isLoading}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={signInForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-foreground">Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none z-10" />
                              <Input
                                {...field}
                                type="password"
                                placeholder="Enter your password"
                                className="pl-10 h-12 border-border focus:border-primary focus:ring-primary"
                                disabled={isLoading}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="text-right -mt-2">
                      <Link
                        to="/forgot-password"
                        className="text-sm text-primary hover:text-primary-hover transition-colors"
                      >
                        Forgot password?
                      </Link>
                    </div>

                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full h-12 bg-gradient-primary hover:shadow-green transition-all duration-200 text-primary-foreground"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Signing In...
                        </>
                      ) : (
                        'Sign In'
                      )}
                    </Button>
                  </form>
                </Form>

                <div className="mt-8 pt-6 border-t border-border text-center">
                  <p className="text-sm text-muted-foreground">
                    Don't have an account?{' '}
                    <button
                      onClick={() => setActiveTab('signup')}
                      className="text-primary font-medium hover:text-primary-hover transition-colors"
                    >
                      Sign up
                    </button>
                  </p>
                </div>
              </TabsContent>

              {/* ── Sign Up Tab ── */}
              <TabsContent value="signup" className="space-y-6 mt-0">
                <div>
                  <h2 className="text-2xl font-bold text-foreground mb-2">Create your account</h2>
                  <p className="text-muted-foreground text-sm mb-6">
                    Start improving your golf game today
                  </p>
                </div>

                <SocialAuthButtons disabled={isLoading} />

                {SOCIAL_AUTH_ENABLED && (
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">Or sign up with email</span>
                    </div>
                  </div>
                )}

                <Form {...signUpForm}>
                  <form onSubmit={signUpForm.handleSubmit(handleSignUp)} className="space-y-5">
                    <div className="grid grid-cols-2 gap-3">
                      <FormField
                        control={signUpForm.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-foreground">First Name</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none z-10" />
                                <Input
                                  {...field}
                                  onChange={(e) => field.onChange(sanitizeInput(e.target.value))}
                                  placeholder="John"
                                  className="pl-10 h-12 border-border focus:border-primary focus:ring-primary"
                                  disabled={isLoading}
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={signUpForm.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-foreground">Last Name</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                onChange={(e) => field.onChange(sanitizeInput(e.target.value))}
                                placeholder="Smith"
                                className="h-12 border-border focus:border-primary focus:ring-primary"
                                disabled={isLoading}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={signUpForm.control}
                      name="nickname"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-foreground">
                            Display Name (Nickname)
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none z-10" />
                              <Input
                                {...field}
                                placeholder="How you want to be displayed"
                                className="pl-10 h-12 border-border focus:border-primary focus:ring-primary"
                                disabled={isLoading}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                          <p className="text-xs text-muted-foreground">
                            This is how your name will appear in the app
                          </p>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={signUpForm.control}
                      name="ageBracket"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-foreground">Age Range</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                            disabled={isLoading}
                          >
                            <FormControl>
                              <SelectTrigger className="h-12 border-border focus:border-primary focus:ring-primary">
                                <SelectValue placeholder="Select your age range" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="under13">Under 13</SelectItem>
                              <SelectItem value="13-17">13–17</SelectItem>
                              <SelectItem value="18+">18+</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                          {signUpForm.watch('ageBracket') === 'under13' && (
                            <p className="text-xs text-destructive font-medium mt-1">
                              You must be at least 13 years old to use No Doubles.
                            </p>
                          )}
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={signUpForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-foreground">Email</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none z-10" />
                              <Input
                                {...field}
                                type="email"
                                placeholder="you@example.com"
                                className="pl-10 h-12 border-border focus:border-primary focus:ring-primary"
                                disabled={isLoading}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={signUpForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-foreground">Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none z-10" />
                              <Input
                                {...field}
                                type="password"
                                placeholder="Create a strong password"
                                className="pl-10 h-12 border-border focus:border-primary focus:ring-primary"
                                minLength={8}
                                disabled={isLoading}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                          <PasswordStrengthMeter password={watchedPassword} onBreachStatusChange={setPasswordBreached} />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={signUpForm.control}
                      name="agreedToTerms"
                      render={({ field }) => (
                        <FormItem className="flex items-start gap-3 space-y-0 rounded-lg bg-muted/50 p-3">
                          <FormControl>
                            <Checkbox
                              checked={field.value === true}
                              onCheckedChange={field.onChange}
                              disabled={isLoading}
                              className="mt-0.5 flex-shrink-0"
                            />
                          </FormControl>
                          <div className="text-xs text-muted-foreground leading-relaxed">
                            By creating an account, you agree to our{' '}
                            <Link to="/terms" target="_blank" className="text-primary hover:underline">
                              Terms of Service
                            </Link>{' '}
                            and{' '}
                            <Link to="/privacy" target="_blank" className="text-primary hover:underline">
                              Privacy Policy
                            </Link>
                            .
                          </div>
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      disabled={isLoading || passwordBreached || signUpForm.watch('ageBracket') === 'under13'}
                      className="w-full h-12 bg-gradient-to-r from-sun-gold to-fairway-light hover:shadow-green transition-all duration-200 text-foreground font-semibold"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Creating Account...
                        </>
                      ) : (
                        <>
                          Create Account
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </form>
                </Form>

                <div className="mt-8 pt-6 border-t border-border text-center">
                  <p className="text-sm text-muted-foreground">
                    Already have an account?{' '}
                    <button
                      onClick={() => setActiveTab('signin')}
                      className="text-primary font-medium hover:text-primary-hover transition-colors"
                    >
                      Sign in
                    </button>
                  </p>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-muted-foreground hover:text-primary transition-colors inline-flex items-center"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to home
          </button>
        </div>
      </div>
    </div>
  );
}
